import React from 'react';
import { Field } from 'formik';
import { useHistory } from 'react-router-dom';
import { TextField as ReactTextField } from '@a-ui/react';

import Box from 'components/Box';
import ConfirmationModal from 'components/ConfirmationModal';
import { DetailLayout } from 'components/DetailLayout';
import { FormErrors } from 'components/Form';
import { FormHelpers } from 'components/Form/Form';
import MultiStepForm, { FormActions, FormStep } from 'components/MultiStepForm';
import PasswordField from 'components/PasswordField';
import Typography from 'components/Typography';
import { api } from 'hooks/api.hooks';
import { translate, translateHTML } from 'i18n';
import { useAuthentication } from 'state/Authentication';
import { useMessage } from 'state/Message';
import { setMessage } from 'state/Message/actions';
import { initialValues, validationSchema } from './config';
import EmailUpdateActions from './EmailUpdateActions';

const EmailUpdate: React.FC = () => {
  const [isModalVisible, setModalVisible] = React.useState(false);
  const { dispatch: dispatchMessage } = useMessage();
  const history = useHistory();
  const {
    state: { user },
    refreshUser,
  } = useAuthentication();

  const handleCancel = () => {
    history.push('/gegevens');
  };

  const handleSubmit = async (values: typeof initialValues, helpers: FormHelpers) => {
    const { password, ...body } = values;

    try {
      await api.put('/access-keys/email', {
        ...body,
        callbackUrl: `${window.location.origin}/gegevens/aanmeldsleutels/e-mail/callback`,
      });

      await refreshUser();

      dispatchMessage(
        setMessage('accessKeys.email.pending.message', 'accessKeysEmail', 'success', {
          email: body?.email!,
        }),
      );
      history.push(`/gegevens/aanmeldsleutels/e-mail?candidate=${body?.email!}`);
    } catch (e) {
      helpers.setFieldError('email', 'error.server.unknown');
    }
  };

  const toggleModal = () => {
    setModalVisible(!isModalVisible);
  };

  return (
    <DetailLayout
      title="accessKeys.email.update.title"
      description="accessKeys.email.update.intro"
      backAction="/gegevens"
      backLabel="accessKeys.overview.title"
    >
      <Box>
        <MultiStepForm onSubmit={handleSubmit} initialValues={initialValues} preventAutoValidation>
          <FormStep validationSchema={validationSchema[0]}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom">
              {translate('accessKeys.password.subtitle')}
            </Typography>

            <FormErrors />

            <PasswordField
              label={translate('input.label.password')}
              name="password"
              classNames="u-margin-bottom"
            />
          </FormStep>

          <FormStep validationSchema={validationSchema[1]}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom">
              {translate('accessKeys.email.update.step2.title')}
            </Typography>

            <Typography classNames="u-margin-bottom">
              {translateHTML('accessKeys.email.update.step2.description')}
            </Typography>

            <FormErrors />

            <Field label={translate('input.label.email')} name="email" as={ReactTextField} />
          </FormStep>

          <FormActions>
            <EmailUpdateActions onCancel={toggleModal} />
          </FormActions>
        </MultiStepForm>
      </Box>

      <ConfirmationModal
        title="accessKeys.email.edit.modal.title"
        body="accessKeys.email.edit.modal.description"
        confirmLabel="common.modal.confirm"
        cancelLabel="common.modal.cancel"
        onRequestClose={toggleModal}
        onConfirm={handleCancel}
        isOpen={isModalVisible}
      />
    </DetailLayout>
  );
};

export default EmailUpdate;
